import ufeApi from 'services/api/ufeApi';
import { gqlClient } from 'services/api/gql/gqlClient';
import { GetNcrOrderDetails } from 'constants/gql/queries/checkout/ncrOrderDetails.gql';
import { OrderDetails } from 'constants/gql/queries/checkout/orderDetails.gql';
import Location from 'utils/Location';
import UrlUtils from 'utils/Url';
import javascriptUtils from 'utils/javascript';
import OrderUtils from 'utils/Order';
import { SMS_NOTIFICATION_STATUS } from 'constants/orderStatus';

const { buildQuery } = UrlUtils;
const { buildMap } = javascriptUtils;

// https://confluence.sephora.com/wiki/display/ILLUMINATE/Get+Order+Details+API
// https://confluence.sephora.com/wiki/display/ILLUMINATE/NCR+Order+Details+API

const DEFAULT_BASKET_TYPE = 'ShipToHome';

const OIS_TO_UFE_REMINDER_STATUS = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    NOT_APPLICABLE: 'notApplicable'
};

const OIS_TO_UFE_ITEM_STATUS = {
    SHIPPED: 'Fulfilled',
    CANCELLED: 'Canceled',
    READY_FOR_PICKUP: 'Reserved',
    PICKED_UP: 'Fulfilled'
};

const normalizeActionFlags = actionFlags => {
    if (!actionFlags) {
        return actionFlags;
    }

    const { backInStockReminderStatus, backInStockPhoneReminderStatus } = actionFlags;

    return {
        ...actionFlags,
        ...(backInStockReminderStatus && {
            backInStockReminderStatus: OIS_TO_UFE_REMINDER_STATUS[backInStockReminderStatus] || backInStockReminderStatus
        }),
        ...(backInStockPhoneReminderStatus && {
            backInStockPhoneReminderStatus: OIS_TO_UFE_REMINDER_STATUS[backInStockPhoneReminderStatus] || backInStockPhoneReminderStatus
        })
    };
};

const normalizeItems = items =>
    (items || []).map(({ substituteSku, status, ...item }) => ({
        ...item,
        ...(substituteSku != null && { substituteSku }),
        ...(status != null && { status: OIS_TO_UFE_ITEM_STATUS[status] || status }),
        sku: {
            ...item.sku,
            productName: item.sku?.productName ?? item.sku?.displayName,
            ...(item.sku?.actionFlags && { actionFlags: normalizeActionFlags(item.sku.actionFlags) })
        }
    }));

const normalizeStates = states => (Array.isArray(states) ? states.map(state => ({ ...state, status: state.status?.toLowerCase() })) : states);

const normalizeDeliveryGroups = shippingMethod => {
    if (!Array.isArray(shippingMethod?.deliveryGroups)) {
        return shippingMethod;
    }

    return {
        ...shippingMethod,
        deliveryGroups: shippingMethod.deliveryGroups.map(group => ({
            ...group,
            items: (Array.isArray(group?.items) ? group.items : []).map(item => (typeof item === 'string' ? { skuId: item } : item))
        }))
    };
};

// OIS returns the HAL (FedEx Hold-At-Location) pick-up location id as `altPickLocationId`,
// but the Address / FetchLocationHours components read `altPickLocationID`. Alias it so the
// FedEx location hours resolve instead of getting stuck on "loading location hours".
const normalizeHalAddress = address => {
    // Only alias when the camelCase id is present and the expected `altPickLocationID` is not
    // already set, so an already-correct value is never clobbered.
    if (!address || address.altPickLocationId == null || address.altPickLocationID != null) {
        return address;
    }

    return { ...address, altPickLocationID: address.altPickLocationId };
};

const sumBasketItemsCount = (items = []) => items.reduce((total, item) => total + (item.qty || 0), 0);

const isBagFeeItem = item => {
    const type = (item?.skuType || item?.sku?.type || '').replace(/[\s_]/g, '').toLowerCase();

    return type === 'bagfee';
};

const SMS_NOTIFICATION_FLAG_STRING_TO_STATUS = {
    NOT_AVAILABLE: SMS_NOTIFICATION_STATUS.NOT_AVAILABLE,
    AVAILABLE: SMS_NOTIFICATION_STATUS.AVAILABLE,
    AVAILABLE_BUT_ENROLLED: SMS_NOTIFICATION_STATUS.AVAILABLE_BUT_ENROLLED
};

const normalizeSmsNotificationFlag = flag =>
    typeof flag === 'string' ? (SMS_NOTIFICATION_FLAG_STRING_TO_STATUS[flag] ?? SMS_NOTIFICATION_STATUS.NOT_AVAILABLE) : flag;

const MDM_DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const toTwelveHourTime = time => {
    const [hour, minute] = (time || '').split(':');
    const parsedHour = Number(hour);

    if (!minute || Number.isNaN(parsedHour)) {
        return '';
    }

    const period = parsedHour >= 12 ? 'PM' : 'AM';
    const twelveHour = parsedHour % 12 === 0 ? 12 : parsedHour % 12;

    return `${String(twelveHour).padStart(2, '0')}:${minute}${period}`;
};

const toDayHours = day => (day?.open && day?.close ? `${toTwelveHourTime(day.open)}-${toTwelveHourTime(day.close)}` : '');

const normalizeStoreDetails = (storeDetails, pickupStore) => {
    if (!storeDetails) {
        return storeDetails;
    }

    const general = storeDetails.general || {};
    const { primaryAddress, regularHours } = general;
    const phone = general.telecoms?.find(telecom => telecom?.type === 'PHONE_1')?.data;
    const storeName = storeDetails.storeName ?? general.storeName ?? pickupStore;

    return {
        ...storeDetails,
        storeName,
        displayName: storeDetails.displayName ?? general.storeName ?? pickupStore,
        storeType: storeDetails.storeType ?? storeDetails.analyticalParameters?.storeType,
        isBopisable: storeDetails.isBopisable ?? storeDetails.omni?.bopisAvailable,
        isCurbsideEnabled: storeDetails.isCurbsideEnabled ?? storeDetails.omni?.curbsideAvailable,
        ...(primaryAddress && {
            address: {
                address1: primaryAddress.streetAddress,
                address2: primaryAddress.suite,
                city: primaryAddress.city,
                state: primaryAddress.state,
                postalCode: primaryAddress.postalCode,
                phone
            }
        }),
        ...(regularHours && {
            storeHours: {
                timeZone: general.timezone,
                ...MDM_DAY_KEYS.reduce((acc, day) => ({ ...acc, [`${day}Hours`]: toDayHours(regularHours[day]) }), {})
            }
        })
    };
};

const normalizePickup = pickup =>
    pickup
        ? {
            ...pickup,
            firstname: pickup.firstname ?? pickup.firstName,
            pickupOrderStates: normalizeStates(pickup.pickupOrderStates),
            storeDetails: normalizeStoreDetails(pickup.storeDetails, pickup.pickupStore)
        }
        : pickup;

const normalizeOisOrderDetails = result => {
    const { SHIPPING_GROUPS, PAYMENT_GROUP_TYPE, ORDER_TRACKING_BUTTON_STATES } = OrderUtils;

    const OIS_TO_UFE_GROUP_TYPE = {
        HARD_GOOD_SHIPPING_GROUP: SHIPPING_GROUPS.HARD_GOOD,
        GIFT_CARD_SHIPPING_GROUP: SHIPPING_GROUPS.GIFT,
        ELECTRONIC_SHIPPING_GROUP: SHIPPING_GROUPS.ELECTRONIC,
        SAME_DAY_SHIPPING_GROUP: SHIPPING_GROUPS.SAME_DAY,
        SDU_ELECTRONIC_SHIPPING_GROUP: SHIPPING_GROUPS.SDU_ELECTRONIC
    };

    const SHIPPING_GROUP_TO_BASKET_TYPE = {
        [SHIPPING_GROUPS.SAME_DAY]: 'SameDay'
    };

    const OIS_TO_UFE_BUTTON_STATE = {
        PENDING: ORDER_TRACKING_BUTTON_STATES.PENDING,
        ACTIVE: ORDER_TRACKING_BUTTON_STATES.ACTIVE,
        DELIVERED: ORDER_TRACKING_BUTTON_STATES.DELIVERED,
        CANCELED: ORDER_TRACKING_BUTTON_STATES.CANCELED
    };

    const entries = result.shippingGroups?.shippingGroupsEntries || [];

    const bagFeeItem = entries.flatMap(entry => entry.shippingGroup?.items || []).find(isBagFeeItem);
    const bagFeeSubtotal = bagFeeItem?.itemTotal;

    const normalizedEntries = entries.map(entry => {
        const groupType = OIS_TO_UFE_GROUP_TYPE[entry.shippingGroupType] || entry.shippingGroupType || SHIPPING_GROUPS.HARD_GOOD;

        return {
            ...entry,
            shippingGroupType: groupType,
            shippingGroup: {
                ...entry.shippingGroup,
                shippingGroupType: groupType,
                address: normalizeHalAddress(entry.shippingGroup?.address),
                shippingMethod: normalizeDeliveryGroups(entry.shippingGroup?.shippingMethod),
                items: normalizeItems((entry.shippingGroup?.items || []).filter(item => !isBagFeeItem(item))),
                sameDayOrderStates: normalizeStates(entry.shippingGroup?.sameDayOrderStates)
            }
        };
    });

    const allItems = normalizedEntries.flatMap(entry => entry.shippingGroup?.items || []);

    const basketsByType = Object.values(
        normalizedEntries.reduce(
            (acc, entry) => {
                const basketType = SHIPPING_GROUP_TO_BASKET_TYPE[entry.shippingGroupType] || DEFAULT_BASKET_TYPE;

                if (!acc[basketType]) {
                    acc[basketType] = { basketType, items: [] };
                }

                acc[basketType].items.push(...(entry.shippingGroup?.items || []));

                return acc;
            },
            { [DEFAULT_BASKET_TYPE]: { basketType: DEFAULT_BASKET_TYPE, items: [] } }
        )
    );

    const itemsByBasket = basketsByType.map(basket => ({
        ...basket,
        itemsCount: sumBasketItemsCount(basket.items)
    }));

    const OIS_TO_UFE_PAYMENT_GROUP_TYPE = {
        CREDIT_CARD_PAYMENT_GROUP: PAYMENT_GROUP_TYPE.CREDIT_CARD,
        STORE_CREDIT_PAYMENT_GROUP: PAYMENT_GROUP_TYPE.STORE_CREDIT,
        GIFT_CARD_PAYMENT_GROUP: PAYMENT_GROUP_TYPE.GIFT_CARD,
        PAYPAL_PAYMENT_GROUP: PAYMENT_GROUP_TYPE.PAYPAL,
        KLARNA_PAYMENT_GROUP: PAYMENT_GROUP_TYPE.KLARNA,
        APPLE_PAY_PAYMENT_GROUP: PAYMENT_GROUP_TYPE.APPLEPAY,
        GOOGLE_PAY_PAYMENT_GROUP: PAYMENT_GROUP_TYPE.GOOGLE_PAY,
        AFTERPAY_PAYMENT_GROUP: PAYMENT_GROUP_TYPE.AFTERPAY,
        VENMO_PAYMENT_GROUP: PAYMENT_GROUP_TYPE.VENMO,
        PAZE_PAYMENT_GROUP: PAYMENT_GROUP_TYPE.PAZE,
        CASH_PAYMENT_GROUP: PAYMENT_GROUP_TYPE.CASH
    };

    const NON_BILLING_PAYMENT_GROUP_TYPES = ['GIFT_CARD_PAYMENT_GROUP', 'STORE_CREDIT_PAYMENT_GROUP'];

    const paymentGroups = result.paymentGroups
        ? {
            ...result.paymentGroups,
            paymentGroupsEntries: (result.paymentGroups.paymentGroupsEntries || []).map(entry => {
                const mappedEntry = {
                    ...entry,
                    paymentGroupType: OIS_TO_UFE_PAYMENT_GROUP_TYPE[entry.paymentGroupType] || entry.paymentGroupType
                };

                if (NON_BILLING_PAYMENT_GROUP_TYPES.includes(entry.paymentGroupType) && mappedEntry.paymentGroup) {
                    const { address: _omitAddress, ...paymentGroup } = mappedEntry.paymentGroup;

                    return { ...mappedEntry, paymentGroup };
                }

                return mappedEntry;
            })
        }
        : result.paymentGroups;

    const normalizedItems = { ...result.items, items: allItems, itemsByBasket };

    if (bagFeeItem && typeof result.items?.itemCount === 'number') {
        normalizedItems.itemCount = result.items.itemCount - 1;
    }

    const normalized = {
        ...result,
        header: {
            ...result.header,
            buttonState: OIS_TO_UFE_BUTTON_STATE[result.header?.buttonState] || result.header?.buttonState,
            smsNotificationFlag: normalizeSmsNotificationFlag(result.header?.smsNotificationFlag)
        },
        items: normalizedItems,
        paymentGroups,
        priceInfo: bagFeeItem ? { ...result.priceInfo, bagFeeSubtotal } : result.priceInfo
    };

    const isPickupOrder = result.header?.isBopisOrder || result.header?.isRopisOrder;

    if (isPickupOrder) {
        return {
            ...normalized,
            pickup: { ...normalizePickup(result.pickup), items: allItems },
            shippingGroups: { ...result.shippingGroups, shippingGroupsEntries: [] }
        };
    }

    return {
        ...normalized,
        pickup: normalizePickup(result.pickup),
        shippingGroups: {
            ...result.shippingGroups,
            shippingGroupsEntries: normalizedEntries
        }
    };
};

/**
 * Builds the query string for the `GET /api/checkout/orders/{orderId}` call —
 * served by Cart Service (order confirmation page) and OXS (order details page).
 *
 * @param {object} params - Named query-param values (all optional). Only truthy values are appended.
 * @param {string} [params.originalOrderId] - Original order ID for NCR reship contexts
 * @param {string} [params.guestEmail] - Normalized guest email for unauthenticated order lookups
 * @param {string} [params.requestOrigin] - Request origin (e.g. order-details / order-confirmation page)
 * @param {boolean} [params.isReshipOrder] - Whether this is a replacement/reship order
 * @param {string} [params.cartType] - Cart type (e.g. 'BOPIS') for Guest BOPIS order routing
 * @returns {string} Query string including the leading `?`. Always non-empty — `includeShippingItems=true`
 *     and `includeProfileFlags=true` are unconditionally included per the endpoint contract.
 */
const buildOrderDetailsQueryParams = ({
    originalOrderId, guestEmail, requestOrigin, isReshipOrder, cartType
}) =>
    buildQuery(
        buildMap({
            includeShippingItems: true,
            includeProfileFlags: true,
            ...(originalOrderId && { originalOrderId }),
            ...(guestEmail && { guestEmail: encodeURIComponent(guestEmail) }),
            ...(requestOrigin && { requestOrigin }),
            ...(isReshipOrder && { isReshipOrder }),
            ...(cartType && { cartType })
        })
    );

/**
 * Fetches order details for the given order ID.
 *
 * Routing priority:
 * 1. NCR replacement orders (isReshipOrder + global + returns flags) → GetNcrOrderDetails GQL
 * 2. Standard order details page (global + orderDetails flags + isOrderDetailsPage) → OrderDetails GQL
 * 3. All other cases → REST fallback: Cart Service on the order confirmation page, OXS on order details page
 *
 * OIS responses are normalized to match the OXS shape expected by downstream Order.js utilities.
 *
 * @param {string} orderId - The order ID to fetch details for
 * @param {(string|string[])} [guestEmail=''] - Guest email for unauthenticated order lookups. Accepts an
 *     array (as returned by UrlUtils.getParams()) which is normalized to the first value.
 * @param {boolean} [isReshipOrder=false] - Whether this is a replacement/reship order
 * @param {boolean} [appendRequestOrigin=false] - Whether to append requestOrigin based on page context
 * @param {string} [originalOrderId=''] - Original order ID for NCR reship contexts
 * @param {string|null} [cartType=null] - Cart type (e.g. 'BOPIS') passed as a query param on the REST call.
 *     Required for Guest BOPIS orders so BE returns the correct order variant.
 * @returns {Promise<Object>} Order details object
 * @throws {Error} Throws when OIS response shape is unexpected
 */
const getOrderDetails = async (
    orderId,
    guestEmail = '',
    isReshipOrder = false,
    appendRequestOrigin = false,
    originalOrderId = '',
    cartType = null
) => {
    const globalEnabled = Sephora?.configurationSettings?.oisMigration?.global?.isEnabled;
    const returnsEnabled = Sephora?.configurationSettings?.oisMigration?.returns?.isEnabled;
    const orderDetailsEnabled = Sephora?.configurationSettings?.oisMigration?.orderDetails?.isEnabled;
    const isOrderDetailsPage = Location.isOrderDetailsPage();

    const normalizedGuestEmail = Array.isArray(guestEmail) ? guestEmail[0] : guestEmail;

    const { requestOrigin } = UrlUtils.getParams();
    const resolvedOrigin =
        isOrderDetailsPage || appendRequestOrigin
            ? requestOrigin?.includes?.(OrderUtils.ORDER_DETAILS_REQUESTS_ORIGIN.ORD_CONFIRMATION_PAGE)
                ? OrderUtils.ORDER_DETAILS_REQUESTS_ORIGIN.ORD_CONFIRMATION_PAGE
                : OrderUtils.ORDER_DETAILS_REQUESTS_ORIGIN.ORD_DETAILS_PAGE
            : '';

    // NCR replacement order path
    if (isReshipOrder && globalEnabled && returnsEnabled) {
        const data = await gqlClient.query({
            operationName: 'GetNcrOrderDetails',
            query: GetNcrOrderDetails,
            variables: {
                input: {
                    orderId,
                    isReshipOrder: true,
                    ...(originalOrderId && { originalOrderId }),
                    ...(resolvedOrigin && { requestOrigin: resolvedOrigin })
                }
            }
        });

        const result = data?.ncrOrderDetails;

        if (!result) {
            throw new Error('Get NCR order details: unexpected response shape');
        }

        return normalizeOisOrderDetails(result);
    }

    if (globalEnabled && orderDetailsEnabled && isOrderDetailsPage) {
        const data = await gqlClient.query({
            operationName: 'OrderDetails',
            query: OrderDetails,
            variables: { input: { orderId, ...(normalizedGuestEmail && { guestEmail: normalizedGuestEmail }) } }
        });

        const result = data?.orderDetails;

        if (!result) {
            throw new Error('Get order details: unexpected response shape');
        }

        return normalizeOisOrderDetails(result);
    }

    // Cart Service (order confirmation page) / OXS (order details page) fallback path
    const queryString = buildOrderDetailsQueryParams({
        originalOrderId,
        guestEmail: normalizedGuestEmail,
        requestOrigin: resolvedOrigin,
        isReshipOrder,
        cartType
    });

    return ufeApi.makeRequest(`/api/checkout/orders/${orderId}${queryString}`, { method: 'GET' });
};

export default getOrderDetails;
